import { HttpParams } from '@angular/common/http';
import { AbstractControl, FormGroup } from '@angular/forms';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { EventBusService } from '@ups/xplat/core';
import { IDynamicModel } from '../../models';
import { dynamicLookupGateEnabled } from '../../utils/dynamic-lookup-gate';

export { dynamicLookupGateEnabled };
import { DYNAMIC_RETRY_LOOKUPS_EVENT } from '../../utils/dynamic-lookup-retry';
import { dynamicRecordIdProperty } from '../../utils/dynamic-value-property';

type Flagged = AbstractControl & { hasPrefillError_dynamic?: boolean };

/** The control `name` in a group; undefined when either is missing. */
export function dynamicGroupControl(
  group: FormGroup | null | undefined,
  name: string | undefined
): AbstractControl | undefined {
  return name ? group?.controls[name] : undefined;
}

/** WI 35277: flag read by dynamicValidatorNoPrefillError (the saved-value lookup failed). */
export function dynamicHasPrefillError(
  control: AbstractControl | null | undefined
): boolean {
  return (
    (control as Flagged | null | undefined)?.hasPrefillError_dynamic ?? false
  );
}

/** Set / clear the prefill-error flag and re-run the control's validators. */
export function dynamicSetPrefillError(
  control: AbstractControl | null | undefined,
  value: boolean
): void {
  if (
    control &&
    dynamicLookupGateEnabled(control) &&
    dynamicHasPrefillError(control) !== value
  ) {
    (control as Flagged).hasPrefillError_dynamic = value;
    control.updateValueAndValidity();
  }
}

/** The search results flagged as the user's favorite (either casing). */
export function dynamicFavorites(data: unknown): Array<unknown> {
  const isFavorite = (item: unknown) =>
    !!(item as { IsUserFavorite?: boolean; isUserFavorite?: boolean })
      ?.IsUserFavorite ||
    (item as { isUserFavorite?: boolean })?.isUserFavorite;
  return Array.isArray(data) && data.length > 0 ? data.filter(isFavorite) : [];
}

/** The only favorite in the results, or null when there are none or several. */
export function dynamicSingleFavorite(data: unknown): unknown | null {
  const favorites = dynamicFavorites(data);
  return favorites.length === 1 ? favorites[0] : null;
}

/** Bug 35174: the search result whose id property (or altId) equals the saved target. */
export function dynamicFindPrefillMatch<T>(
  result: Array<T>,
  target: unknown,
  config: IDynamicModel
): T | undefined {
  const normalizedTarget = target?.toString()?.toLowerCase();
  if (!normalizedTarget) {
    return undefined;
  }
  return result.find((record) => {
    const item = record as Record<string, unknown>;
    const idProperty = dynamicRecordIdProperty(item, config);
    const primaryValue = (idProperty ? item[idProperty] : null)
      ?.toString()
      ?.toLowerCase();
    const altValue = item?.['altId']
      ? (item['altId'] as string)?.toString()?.toLowerCase()
      : null;
    return primaryValue === normalizedTarget || altValue === normalizedTarget;
  });
}

/** Query params of one remote search: scope default, custom params, then the value query. */
export function dynamicSearchParams(
  scope: { query?: string; paramName?: string },
  customQueryParams: unknown,
  value: { paramName?: string; query?: string }
): HttpParams {
  let params = new HttpParams();
  if (scope.query && scope.paramName) {
    params = params.append(scope.paramName, scope.query);
  }
  const custom = (customQueryParams ?? {}) as Record<string, string>;
  for (const key of Object.keys(custom)) {
    params = params.append(key, custom[key]);
  }
  if (value.paramName && value.query) {
    params = params.append(value.paramName, value.query);
  }
  return params;
}

/** WI 35277: re-run a failed saved-value lookup on the host's Retry event; torn down with `destroy$`. */
export function dynamicObserveLookupRetry(
  eventBus: EventBusService,
  destroy$: Observable<unknown>,
  getControl: () => AbstractControl | null | undefined,
  retry: () => void
): void {
  eventBus
    .observe(DYNAMIC_RETRY_LOOKUPS_EVENT)
    .pipe(takeUntil(destroy$))
    .subscribe(() => {
      if (dynamicHasPrefillError(getControl())) {
        retry();
      }
    });
}
